import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useT, useUiStore } from '@/store';
import { api, API_BASE } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import {
  useOrder, useOrderDwellRecords, useOrderEvents, useOrderPriceEstimate,
  useSendOrderTelegram, useSetDwellReason, useSetPricingMethod, useUpdateOrder,
  useWaiveDwellRecord,
} from '@/hooks/useOrders';
import { Modal } from '@/components/ui/Overlay';
import { ArchivedBadge } from '@/components/ui/ArchivedBadge';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ErrorState, SkeletonRows } from '@/components/ui/Panel';
import { eurExact, formatOrderDateTime, waybillLabel } from '@/lib/format';
import { DWELL_REASONS } from '@/lib/dwellReasons';
import { OrderRoute } from './OrderRouteMap';
import { TruckRouteSection } from './TruckRouteSection';

/**
 * V-73. The waybill fields this form owns.
 *
 * The console could capture three of the seventeen the route accepts, and the
 * two it could not that cost real money are unit_price and total_sum: without
 * them the purchase-side comparison (PurchasePriceCheck below, V-66) has
 * nothing to compare, so on every hand-entered load — which is exactly the
 * load where OCR failed and a human is already holding the paper — a quarry
 * price rise nobody told the office about goes unnoticed. Measured: 21,12 EUR
 * on one load, and the check simply could not run.
 *
 * Sender and receiver are here because core/pdf_export.py prints both on the
 * delivery note the CLIENT receives, and a hand-entered load printed "—" for
 * each. The rest of the paper's fields (carrier, loading/unloading place,
 * unit of measure, other data, issued by, VAT-inclusive total) are
 * deliberately NOT here: the carrier is this tenant, the two places are the
 * order's own quarry and site, tonnes is the only unit this product deals in,
 * and a box for a field that changes no outcome is how the boxes that do stop
 * being filled. The route leaves every column this form omits untouched.
 */
const WAYBILL_TEXT_FIELDS = [
  'waybill_number', 'truck_plate', 'sender_name', 'receiver_name',
  'issue_date', 'issue_place', 'trailer_plate', 'driver_name',
];
const EMPTY_WAYBILL = {
  waybill_number: '', quantity: '', truck_plate: '', unit_price: '', total_sum: '',
  sender_name: '', receiver_name: '', issue_date: '', issue_place: '',
  trailer_plate: '', driver_name: '',
};

/** '' clears the column; anything unparseable is not a number the paper said. */
function numberOrNull(raw) {
  const value = Number.parseFloat(raw);
  return Number.isNaN(value) ? null : value;
}

/**
 * What the paper's own arithmetic comes to, when both halves were typed.
 *
 * A transcription slip in either box becomes a false "the quarry overcharged
 * us" the next time PurchasePriceCheck runs, and the cheapest moment to catch
 * it is while the dispatcher still has the ticket in their hand. A warning
 * and never a block: a real waybill can carry a discount line or its own
 * rounding, and refusing to record what the paper says would be worse than
 * recording a figure that disagrees with a multiplication.
 */
function waybillArithmetic({ unit_price: unitPrice, quantity, total_sum: totalSum }) {
  const rate = numberOrNull(unitPrice);
  const tons = numberOrNull(quantity);
  const total = numberOrNull(totalSum);
  if (rate === null || tons === null || total === null) return null;
  // Compared in whole cents, not in euros: Math.abs(279.84 - 279.85) is
  // 0.010000000000047748 in binary floating point, so a euro comparison
  // against 0.01 calls a one-cent rounding a typo — which is how a warning
  // that cries wolf teaches a dispatcher to click past the real ones.
  const computedCents = Math.round(rate * tons * 100);
  const totalCents = Math.round(total * 100);
  // A cent either way is rounding on the ticket, not a slip at the keyboard.
  return Math.abs(computedCents - totalCents) <= 1
    ? null
    : { computed: computedCents / 100, total: totalCents / 100 };
}

/**
 * Waybill inspector: the driver's ticket photo side by side with what OCR
 * read off it, plus manual correction.
 *
 * The confidence score is shown rather than hidden, and colored on the same
 * thresholds the backend uses to decide manual_review_needed, because the
 * dispatcher's actual job at this modal is to decide whether to trust the
 * number — a bare weight with no provenance invites exactly the wrong
 * answer. Below 50% the manual-entry form is opened by default instead of
 * making them find the button.
 *
 * POST /api/orders/<id>/waybill records the correction as a manual entry
 * (waybill_source='manual', manual_review_needed=0) — a human vouching for
 * the number, which is why the backend then stops second-guessing it.
 */
export function OCRInspectionModal({ orderId, onClose }) {
  const t = useT();
  const lang = useUiStore((s) => s.lang);
  const queryClient = useQueryClient();
  const { data: order, isLoading, isError, error } = useOrder(orderId);

  const [manualOpen, setManualOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [values, setValues] = useState(EMPTY_WAYBILL);
  const [validationError, setValidationError] = useState('');

  // Same waybill_source gate as the client portal, and for the same reason:
  // a hand-typed weight in a database written before this was fixed carries
  // a fabricated 1.0, and a dispatcher reading "100%" beside a number their
  // colleague typed would trust it as a machine reading.
  const isMachineRead = order?.waybill_source === 'ocr';
  const confidencePct =
    isMachineRead && order?.ocr_confidence_score != null
      ? Math.round(order.ocr_confidence_score * 100)
      : null;

  // V-199. Seeded once per INSPECTION (a distinct orderId becoming open),
  // not once per `order` reference. useOrder(orderId) refetches on window
  // focus and this modal's own OCRInspectionModal-shaped state outlives any
  // single order — the dispatcher can hold it open across many background
  // refetches while correcting the form by hand. A write that touches this
  // exact row for a reason unrelated to the waybill (blueprints/invoices.py
  // stamping invoice_id/updated_at when someone else issues the day's
  // invoice batch, which covers every delivered order with no exclusion for
  // one currently open here) still gives TanStack Query's structural
  // sharing a new top-level object, since not every field matched the
  // cached one — and the effect used to fire on that alone, silently
  // discarding whatever the dispatcher had half-typed with no toast, no
  // warning, no visual diff.
  const seededForOrderId = useRef(null);
  useEffect(() => {
    if (!orderId) {
      seededForOrderId.current = null;
      return;
    }
    if (!order || seededForOrderId.current === orderId) return;
    seededForOrderId.current = orderId;
    setValues({
      waybill_number: order.waybill_number ?? '',
      quantity: order.actual_ocr_weight_tons ?? '',
      truck_plate: order.truck_plate ?? '',
      unit_price: order.unit_price ?? '',
      total_sum: order.total_sum ?? '',
      sender_name: order.sender_name ?? '',
      receiver_name: order.receiver_name ?? '',
      issue_date: order.waybill_issue_date ?? '',
      issue_place: order.waybill_issue_place ?? '',
      trailer_plate: order.trailer_plate ?? '',
      driver_name: order.driver_name_on_waybill ?? '',
    });
    setMoreOpen(false);
    setValidationError('');
    // A low-confidence read is the case this modal exists for: open the
    // correction form straight away rather than making it a second click.
    // Only a real machine read can be low-confidence — an already
    // hand-entered order has nothing left to correct from a score.
    setManualOpen(
      order.waybill_source === 'ocr'
      && order.ocr_confidence_score != null
      && order.ocr_confidence_score < 0.5);
  }, [order, orderId]);

  const saveWaybill = useMutation({
    mutationFn: (payload) => api(`/api/orders/${orderId}/waybill`, { method: 'POST', body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.order(orderId) });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  const submitManual = () => {
    const quantity = Number.parseFloat(values.quantity);
    if (Number.isNaN(quantity)) {
      setValidationError(t('modal.enterValidQuantity'));
      return;
    }
    setValidationError('');
    // Every field this form OWNS is sent, emptied ones as null so clearing a
    // wrong OCR read works. Fields the form does not show are not sent at
    // all, and the route leaves those columns exactly as they were — see
    // WAYBILL_FIELDS in blueprints/orders.py. Sending the whole set blindly
    // is what used to destroy fifteen columns on every weight correction.
    saveWaybill.mutate({
      quantity,
      ...Object.fromEntries(WAYBILL_TEXT_FIELDS.map((f) => [f, values[f].trim() || null])),
      unit_price: numberOrNull(values.unit_price),
      total_sum: numberOrNull(values.total_sum),
    });
  };

  const arithmetic = waybillArithmetic(values);
  const photoUrl = order?.ticket_photo_url ? `${API_BASE}${order.ticket_photo_url}` : null;
  const confidenceClass =
    confidencePct == null ? 'text-dim' : confidencePct >= 80 ? 'text-green' : confidencePct >= 50 ? 'text-amber' : 'text-red';

  return (
    <Modal
      open={Boolean(orderId)}
      onClose={onClose}
      width="max-w-[620px]"
      title={order ? waybillLabel(order) : t('orders.waybillBtn')}
    >
      {isLoading ? <SkeletonRows rows={5} /> : null}
      {isError ? <ErrorState error={error} /> : null}

      {order ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-dim">
            <StatusBadge status={order.order_status} />
            <span>{formatOrderDateTime(order.created_at, lang)}</span>
            <span>
              {order.quarry_name}
              {order.quarry_active === 0 ? <ArchivedBadge deletedAt={order.quarry_deleted_at} /> : null}
              {order.quarry_name && order.site_name ? ' → ' : null}
              {order.site_name}
              {order.site_active === 0 ? <ArchivedBadge deletedAt={order.site_deleted_at} /> : null}
            </span>
          </div>

          <section>
            <h3 className="panel-title">{t('modal.ocrHeading')}</h3>
            {photoUrl ? (
              <a href={photoUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={photoUrl}
                  alt={t('driver.uploadWaybill')}
                  loading="lazy"
                  className="block max-h-[240px] w-full rounded-[10px] border border-line bg-white/2 object-contain"
                />
              </a>
            ) : (
              <p className="text-[11px] text-dim">{t('modal.noTicketPhotoYet')}</p>
            )}

            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px]">
              <span className={`font-semibold ${confidenceClass}`}>
                {confidencePct != null
                  ? `${t('modal.confidence')} ${confidencePct}%`
                  : t('modal.confidenceUnknown')}
              </span>
              {order.manual_review_needed ? (
                <span className="rounded-full bg-amber/15 px-2.5 py-1 text-[9.5px] font-bold uppercase text-amber">
                  {t('modal.manualReviewFlag')}
                </span>
              ) : null}
            </div>
            {order.waybill_source ? (
              <p className="mt-1 text-[10.5px] text-dim">
                {t('modal.waybillSource')}:{' '}
                {order.waybill_source === 'manual' ? t('common.manually') : 'OCR'}
              </p>
            ) : null}
          </section>

          <section className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
            <ReadRow label={t('driver.waybillNr')} value={order.waybill_number} />
            <ReadRow label={t('driver.cargo')} value={order.cargo_type} />
            <ReadRow
              label={t('driver.quantity')}
              value={
                order.actual_ocr_weight_tons
                  ? `${order.actual_ocr_weight_tons} t (${t('modal.weightSourceOcr')})`
                  : order.estimated_weight_tons
                    ? `${order.estimated_weight_tons} t (${t('modal.weightSourceEstimated')})`
                    : null
              }
            />
            <ReadRow label={t('driver.sender')} value={order.sender_name} />
            <ReadRow label={t('driver.receiver')} value={order.receiver_name} />
            <ReadRow label={t('modal.truckPlatePh')} value={order.truck_plate} />
          </section>

          <section>
            {!manualOpen ? (
              <button type="button" className="btn w-full" onClick={() => setManualOpen(true)}>
                {t('modal.enterWaybillManually')}
              </button>
            ) : (
              <div className="space-y-2.5 rounded-[11px] border border-line bg-white/2 p-3">
                <h3 className="panel-title !mb-0">{t('modal.waybillManualHeading')}</h3>
                <p className="text-[10.5px] leading-snug text-dim">{t('modal.waybillHint')}</p>
                <input
                  className="field"
                  value={values.waybill_number}
                  onChange={(e) => setValues((v) => ({ ...v, waybill_number: e.target.value }))}
                  placeholder={t('modal.waybillNrPh')}
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="field"
                    type="number"
                    step="0.1"
                    min="0"
                    value={values.quantity}
                    onChange={(e) => setValues((v) => ({ ...v, quantity: e.target.value }))}
                    placeholder={t('modal.quantityPh')}
                  />
                  <input
                    className="field"
                    value={values.truck_plate}
                    onChange={(e) => setValues((v) => ({ ...v, truck_plate: e.target.value }))}
                    placeholder={t('modal.truckPlatePh')}
                  />
                </div>

                {/* The two the purchase-side check cannot run without, and
                    the reason they are not behind the expander below: they
                    are the money on this piece of paper. */}
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="field"
                    type="number"
                    step="0.01"
                    min="0"
                    value={values.unit_price}
                    onChange={(e) => setValues((v) => ({ ...v, unit_price: e.target.value }))}
                    placeholder={t('modal.unitPricePh')}
                  />
                  <input
                    className="field"
                    type="number"
                    step="0.01"
                    min="0"
                    value={values.total_sum}
                    onChange={(e) => setValues((v) => ({ ...v, total_sum: e.target.value }))}
                    placeholder={t('modal.totalSumPh')}
                  />
                </div>
                {arithmetic ? (
                  <p role="status" className="text-[11px] leading-snug text-amber">
                    {t('modal.waybillArithmetic', {
                      computed: eurExact(arithmetic.computed),
                      total: eurExact(arithmetic.total),
                    })}
                  </p>
                ) : null}

                {!moreOpen ? (
                  <button
                    type="button"
                    className="btn w-full !text-[11px]"
                    onClick={() => setMoreOpen(true)}
                  >
                    {t('modal.waybillMoreBtn')}
                  </button>
                ) : (
                  <div className="space-y-2.5 border-t border-line pt-2.5">
                    {/* Both of these are printed on the delivery note the
                        client receives; a hand-entered load printed "—". */}
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        className="field"
                        value={values.sender_name}
                        onChange={(e) => setValues((v) => ({ ...v, sender_name: e.target.value }))}
                        placeholder={t('modal.senderPh')}
                      />
                      <input
                        className="field"
                        value={values.receiver_name}
                        onChange={(e) => setValues((v) => ({ ...v, receiver_name: e.target.value }))}
                        placeholder={t('modal.receiverPh')}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        className="field"
                        type="date"
                        value={values.issue_date}
                        onChange={(e) => setValues((v) => ({ ...v, issue_date: e.target.value }))}
                        aria-label={t('modal.issueDatePh')}
                      />
                      <input
                        className="field"
                        value={values.issue_place}
                        onChange={(e) => setValues((v) => ({ ...v, issue_place: e.target.value }))}
                        placeholder={t('modal.issuePlacePh')}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        className="field"
                        value={values.trailer_plate}
                        onChange={(e) => setValues((v) => ({ ...v, trailer_plate: e.target.value }))}
                        placeholder={t('modal.trailerPlatePh')}
                      />
                      <input
                        className="field"
                        value={values.driver_name}
                        onChange={(e) => setValues((v) => ({ ...v, driver_name: e.target.value }))}
                        placeholder={t('modal.driverOnWaybillPh')}
                      />
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-primary w-full"
                  onClick={submitManual}
                  disabled={saveWaybill.isPending}
                >
                  {t(saveWaybill.isPending ? 'common.loading' : 'modal.saveWaybill')}
                </button>
                {validationError ? <ErrorState>{validationError}</ErrorState> : null}
                {saveWaybill.isError ? <ErrorState error={saveWaybill.error} /> : null}
                {saveWaybill.isSuccess ? (
                  <p className="text-[11.5px] text-green">{t('modal.waybillEnteredCheck')}</p>
                ) : null}
              </div>
            )}
          </section>

          <AgreedRateSection order={order} orderId={orderId} />

          <PricingMethodSection order={order} orderId={orderId} />

          <DriverMessage order={order} orderId={orderId} />

          <PurchasePriceCheck order={order} orderId={orderId} />

          <OrderStops orderId={orderId} lang={lang} />

          {/* The plan, then the record: can this truck legally make this
              delivery at all, immediately above what it actually did. */}
          <TruckRouteSection orderId={orderId} />

          {/* Directly under the stops it backs up: the stop list says what a
              client is being charged for, this says what recorded it. */}
          <OrderRoute orderId={orderId} order={order} lang={lang} />

          <OrderHistory orderId={orderId} lang={lang} />
        </div>
      ) : null}
    </Modal>
  );
}

/**
 * The other half of the multiplication.
 *
 * An invoice line is RATE x WEIGHT. This modal has always owned the weight
 * half — the ticket photo, what OCR read, the manual correction — and the
 * rate half had nowhere to live at all: until V-34 the invoice took it from
 * the tenant's price list and nothing else, and until V-35 the only field
 * that could override it sat on the B2B portal's approval form, which is
 * the path fewest loads take. A dispatcher who agreed 11,00 on the phone had
 * no way to record it against the load he agreed it for.
 *
 * IT IS EDITABLE AFTER DELIVERY, and deliberately. PUT /api/orders/<id>
 * refuses to touch a delivered order and is right to — its driver, quarry,
 * site and weight are the historical record. A rate is not history; it is a
 * commercial term nothing has acted on until an invoice is issued, and the
 * moment a dispatcher most often notices it is missing is when he opens the
 * draft after delivery. The line is drawn where the money sets, not where
 * the truck stops: once the load is on an invoice, the rate is frozen with
 * it, and the server says why.
 *
 * BLANK IS A REAL ANSWER, not a missing one. It means the price list
 * decides, which is what every load created before this field existed
 * means. Clearing the box is how a dispatcher takes a rate off the wrong
 * load — typing today's list price by hand instead would freeze today's
 * list onto a load that should follow it.
 */
function AgreedRateSection({ order, orderId }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [transportDraft, setTransportDraft] = useState('');
  const [pricingMode, setPricingMode] = useState('combined');
  const [billToClient, setBillToClient] = useState(true);
  const [seeded, setSeeded] = useState(null);
  const [typo, setTypo] = useState(false);
  const [transportTypo, setTransportTypo] = useState(false);

  const save = useMutation({
    mutationFn: (payload) =>
      api(`/api/orders/${orderId}/agreed-price`, { method: 'POST', body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.order(orderId) });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-draft'] });
    },
  });

  if (seeded !== orderId) {
    setSeeded(orderId);
    setDraft(order.agreed_price_per_ton != null ? String(order.agreed_price_per_ton) : '');
    setTransportDraft(order.transport_price_eur != null ? String(order.transport_price_eur) : '');
    setPricingMode(order.pricing_mode || 'combined');
    setBillToClient(order.bill_to_client !== false && order.bill_to_client !== 0);
  }

  const billed = order.invoice_id != null;
  const submit = () => {
    // Blank -> null, which CLEARS the rate/charge and hands the load back
    // to the price list / to having no separate transport charge. Never 0:
    // Number('') is 0 in JavaScript, and a 0 in either column bills a full
    // truckload — or its delivery — at nothing.
    const rateText = draft.trim().replace(',', '.');
    const transportText = transportDraft.trim().replace(',', '.');
    const rateBlank = rateText === '';
    const transportBlank = transportText === '';
    const rateParsed = rateBlank ? null : Number(rateText);
    const transportParsed = transportBlank ? null : Number(transportText);
    // A typo must not be sent. JSON.stringify(NaN) is `null`, so "11o0"
    // would arrive as a clear — silently wiping a value the dispatcher was
    // trying to correct, with a 200 and no sign anything went wrong.
    const rateTypo = !rateBlank && !Number.isFinite(rateParsed);
    const transportTypoNow = !transportBlank && !Number.isFinite(transportParsed);
    setTypo(rateTypo);
    setTransportTypo(transportTypoNow);
    if (rateTypo || transportTypoNow) return;
    save.mutate({
      agreed_price_per_ton: rateParsed,
      transport_price_eur: transportParsed,
      // Meaningless without a transport charge — sent anyway so the value
      // shown on screen is exactly the value that gets stored.
      pricing_mode: pricingMode,
      bill_to_client: billToClient,
    });
  };

  return (
    <section className="space-y-2 rounded-[11px] border border-line bg-white/2 p-3">
      <h3 className="panel-title !mb-0">{t('modal.agreedRateHeading')}</h3>
      <p className="text-[10.5px] leading-snug text-dim">
        {order.agreed_price_per_ton != null
          /* eurExact, not the raw number: 12.5 on a Lithuanian screen is a
             typo, and this sentence is about money. It carries the symbol,
             so the string says "/t" rather than "EUR/t". */
          ? t('modal.agreedRateSet', { rate: eurExact(order.agreed_price_per_ton) })
          : t('modal.agreedRateFromList')}
      </p>
      {/* V-78. Where the number came from, which is what schema.sql asks a
          screen to do: agreed_price_source exists "so a screen can say it
          rather than leaving '11.00' to look like it fell out of the
          system". No screen said it.

          The distinction is not cosmetic. 'dispatcher' is the carrier's own
          number. 'client_request' is a rate the CLIENT proposed in their
          portal and a dispatcher accepted on the approval form — which may
          have been checked against the price list, or may not.

          THREE states, not two. A rate recorded before this column existed
          has no source, and answering "dispatcher" for it would invent a
          fact about who agreed what. Measured on a live database: 6 loads
          carry a rate with no source and 2 carry 'dispatcher'. Same rule as
          invoices.send_tracked next door, whose third state is "not known"
          for exactly this reason. */}
      {order.agreed_price_per_ton != null ? (
        <p className="text-[10.5px] leading-snug text-dim">
          {t(order.agreed_price_source
            ? `modal.agreedRateFrom.${order.agreed_price_source}`
            : 'modal.agreedRateFromUnknown')}
        </p>
      ) : null}
      {billed ? (
        /* Not a disabled box with no explanation: the way out exists (cancel
           the invoice, correct, re-issue) and the dispatcher has to be told
           it rather than concluding the field is broken. */
        <p className="text-[11px] leading-snug text-amber" role="note">
          {t('modal.agreedRateLocked')}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <input
              className="field"
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('modal.agreedRatePh')}
              aria-label={t('modal.agreedRateHeading')}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={submit}
              disabled={save.isPending}
            >
              {t(save.isPending ? 'common.loading' : 'common.saveBtn')}
            </button>
          </div>
          <p className="text-[10.5px] leading-snug text-dim">{t('modal.agreedRateHint')}</p>
          {typo ? <ErrorState>{t('modal.agreedRateNotANumber')}</ErrorState> : null}

          <h3 className="panel-title !mb-0 !mt-3">{t('modal.transportPriceHeading')}</h3>
          <input
            className="field"
            inputMode="decimal"
            value={transportDraft}
            onChange={(e) => setTransportDraft(e.target.value)}
            placeholder={t('modal.transportPricePh')}
            aria-label={t('modal.transportPriceHeading')}
          />
          <p className="text-[10.5px] leading-snug text-dim">{t('modal.transportPriceHint')}</p>
          {transportTypo ? <ErrorState>{t('modal.transportPriceNotANumber')}</ErrorState> : null}

          {/* Meaningless while transportDraft is blank — a mode with
              nothing to itemize — so it stays hidden until there is
              something for it to apply to. */}
          {transportDraft.trim() !== '' ? (
            <div className="space-y-1">
              <div className="flex gap-3 text-[11.5px]">
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name={`pricing-mode-${orderId}`}
                    checked={pricingMode === 'combined'}
                    onChange={() => setPricingMode('combined')}
                  />
                  {t('modal.pricingModeCombined')}
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name={`pricing-mode-${orderId}`}
                    checked={pricingMode === 'itemized'}
                    onChange={() => setPricingMode('itemized')}
                  />
                  {t('modal.pricingModeItemized')}
                </label>
              </div>
              <p className="text-[10.5px] leading-snug text-dim">{t('modal.pricingModeHint')}</p>
            </div>
          ) : null}

          <label className="flex items-center gap-1.5 text-[11.5px]">
            <input
              type="checkbox"
              checked={billToClient}
              onChange={(e) => setBillToClient(e.target.checked)}
            />
            {t('modal.billToClientLabel')}
          </label>
          <p className="text-[10.5px] leading-snug text-dim">{t('modal.billToClientHint')}</p>

          {save.isError ? <ErrorState error={save.error} /> : null}
        </>
      )}
    </section>
  );
}

/**
 * Transporto kainodaros modeliai (V-pricing-methods).
 *
 * Same "editable after delivery, locked once invoiced" shape as
 * AgreedRateSection just above — a pricing method is a commercial term,
 * not history, and the moment a dispatcher discovers it should have been
 * per_km instead of the price-list default is usually after delivery, when
 * the invoice draft is open. POST .../pricing-method clears every other
 * method's fields server-side on write, so this form only ever sends the
 * fields the CURRENTLY selected method needs.
 *
 * tonkm_capacity_tons defaults from order.vehicle_capacity_tons when the
 * dispatcher switches to 'ton_km' and the field is still blank — a
 * convenience, not a lock: the number stays editable, since the business's
 * own ton_km model prices the DECLARED capacity, which is not always the
 * same figure as whatever the vehicle record happens to carry.
 */
const PRICING_METHOD_NOTE_KEYS = {
  per_ton: 'modal.pricingNotePerTon',
  per_km: 'modal.pricingNotePerKm',
  flat: 'modal.pricingNoteFlat',
  hourly: 'modal.pricingNoteHourly',
  ton_km: 'modal.pricingNoteTonKm',
};

function PricingMethodSection({ order, orderId }) {
  const t = useT();
  const setMethod = useSetPricingMethod(orderId);
  const { data: estimate } = useOrderPriceEstimate(orderId);
  const [seeded, setSeeded] = useState(null);
  const [form, setForm] = useState(null);

  const seedFrom = (o) => ({
    pricing_method: o.pricing_method || 'per_ton',
    distance_km_one_way: o.distance_km_one_way != null ? String(o.distance_km_one_way) : '',
    is_round_trip: Boolean(o.is_round_trip),
    price_per_km: o.price_per_km != null ? String(o.price_per_km) : '',
    flat_price_eur: o.flat_price_eur != null ? String(o.flat_price_eur) : '',
    hourly_rate_eur: o.hourly_rate_eur != null ? String(o.hourly_rate_eur) : '',
    billed_hours: o.billed_hours != null ? String(o.billed_hours) : '',
    tonkm_capacity_tons: o.tonkm_capacity_tons != null ? String(o.tonkm_capacity_tons)
      : (o.vehicle_capacity_tons != null ? String(o.vehicle_capacity_tons) : ''),
  });

  if (seeded !== orderId) {
    setSeeded(orderId);
    setForm(seedFrom(order));
  }
  if (!form) return null;

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const billed = order.invoice_id != null;
  const num = (text) => {
    const trimmed = String(text ?? '').trim().replace(',', '.');
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined; // undefined marks a typo
  };

  const submit = () => {
    const method = form.pricing_method;
    const payload = { pricing_method: method };
    if (method === 'per_km') {
      payload.distance_km_one_way = num(form.distance_km_one_way);
      payload.is_round_trip = form.is_round_trip;
      payload.price_per_km = num(form.price_per_km);
    } else if (method === 'flat') {
      payload.flat_price_eur = num(form.flat_price_eur);
    } else if (method === 'hourly') {
      payload.hourly_rate_eur = num(form.hourly_rate_eur);
      payload.billed_hours = num(form.billed_hours);
    } else if (method === 'ton_km') {
      payload.tonkm_capacity_tons = num(form.tonkm_capacity_tons);
      payload.distance_km_one_way = num(form.distance_km_one_way);
    }
    if (Object.values(payload).some((v) => v === undefined)) return; // a typo somewhere — don't send
    setMethod.mutate(payload);
  };

  return (
    <section className="space-y-2 rounded-[11px] border border-line bg-white/2 p-3">
      <h3 className="panel-title !mb-0">{t('modal.pricingMethodHeading')}</h3>
      {billed ? (
        <p className="text-[11px] leading-snug text-amber" role="note">
          {t('modal.pricingMethodLocked')}
        </p>
      ) : (
        <>
          <select
            className="field"
            value={form.pricing_method}
            onChange={set('pricing_method')}
            aria-label={t('modal.pricingMethodHeading')}
          >
            {['per_ton', 'per_km', 'flat', 'hourly', 'ton_km'].map((m) => (
              <option key={m} value={m}>{t(`pricingMethod.${m}`)}</option>
            ))}
          </select>

          {form.pricing_method === 'per_km' ? (
            <div className="grid grid-cols-2 gap-2">
              <input
                className="field" inputMode="decimal"
                value={form.distance_km_one_way} onChange={set('distance_km_one_way')}
                placeholder={t('modal.pricingDistancePh')}
              />
              <input
                className="field" inputMode="decimal"
                value={form.price_per_km} onChange={set('price_per_km')}
                placeholder={t('modal.pricingRatePerKmPh')}
              />
              <label className="col-span-2 flex items-center gap-2 text-[11.5px]">
                <input type="checkbox" checked={form.is_round_trip} onChange={set('is_round_trip')} />
                {t('modal.pricingRoundTrip')}
              </label>
            </div>
          ) : null}

          {form.pricing_method === 'flat' ? (
            <input
              className="field" inputMode="decimal"
              value={form.flat_price_eur} onChange={set('flat_price_eur')}
              placeholder={t('modal.pricingFlatPh')}
            />
          ) : null}

          {form.pricing_method === 'hourly' ? (
            <div className="grid grid-cols-2 gap-2">
              <input
                className="field" inputMode="decimal"
                value={form.hourly_rate_eur} onChange={set('hourly_rate_eur')}
                placeholder={t('modal.pricingHourlyRatePh')}
              />
              <input
                className="field" inputMode="decimal"
                value={form.billed_hours} onChange={set('billed_hours')}
                placeholder={t('modal.pricingBilledHoursPh')}
              />
            </div>
          ) : null}

          {form.pricing_method === 'ton_km' ? (
            <div className="grid grid-cols-2 gap-2">
              <input
                className="field" inputMode="decimal"
                value={form.tonkm_capacity_tons} onChange={set('tonkm_capacity_tons')}
                placeholder={t('modal.pricingCapacityPh')}
              />
              <input
                className="field" inputMode="decimal"
                value={form.distance_km_one_way} onChange={set('distance_km_one_way')}
                placeholder={t('modal.pricingDistancePh')}
              />
            </div>
          ) : null}

          {/* What the price above is computed from — same explanatory-note
              style as the "Sutartas įkainis" helper text above this section,
              so a dispatcher never has to guess what the numbers mean. */}
          <p className="text-[11px] leading-snug text-dim">
            {t(PRICING_METHOD_NOTE_KEYS[form.pricing_method])}
          </p>

          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={setMethod.isPending}
          >
            {t(setMethod.isPending ? 'common.loading' : 'common.saveBtn')}
          </button>

          {estimate?.price_be_pvm != null ? (
            <p className="text-[10.5px] leading-snug text-dim">
              {t('modal.pricingEstimate', { amount: eurExact(estimate.price_be_pvm) })}
            </p>
          ) : null}
          {setMethod.isError ? <ErrorState error={setMethod.error} /> : null}
        </>
      )}
    </section>
  );
}

/**
 * Who did what to this order, and when (P1-13).
 *
 * The endpoint behind this has existed for a long time and nothing in the
 * console called it, so the trail was reachable only with curl — which is
 * its own version of the finding: "who changed that tonnage" could not be
 * asked, let alone answered. It lives in THIS modal because this is the
 * screen a dispatcher opens when they are already asking about a number on
 * an order.
 *
 * An unnamed action says "not recorded" rather than "administrator". It is
 * the honest word for three real cases — the company API key, a background
 * job, and every row written before the trail could record a person — and
 * a name printed there would be one somebody invented.
 */
/**
 * KAS perskaitė važtaraštį, ir ar skaičius įrodytas.
 *
 * Eilutėje `orders` guli tik pats svoris; kaip prie jo prieita, gyvena
 * įvykio detalėse (app.py `ticket_ocr`), ir būtent todėl ši žymė yra čia,
 * o ne prie skaičiaus viršuje — laikas ir šaltinis yra juostos klausimai.
 *
 * `weights_verified` reiškia, kad kvito paties trys svoriai patikrino
 * vienas kitą (bruto − tara = neto). Tai vienintelė vieta konsolėje, kur
 * dispečeris gali pamatyti, kodėl 68% patikimumo nuskaitymas NEPAPRAŠĖ
 * rankinės peržiūros — be jos ta išimtis atrodytų kaip klaida.
 *
 * Nieko nerodo įvykiui be `detail.reader`: kiekvienas užsakymas, nuskaitytas
 * iki šio darbo, tokios detalės neturi, ir "nežinoma" ten būtų teiginys apie
 * praeitį, kurio niekas nedarė.
 */
function ReaderNote({ detail }) {
  const t = useT();
  const reader = detail && typeof detail === 'object' ? detail.reader : null;
  if (!reader) return null;
  const verified = detail.weights_verified === true;
  return (
    <>
      <span className="ml-1.5 font-normal text-dim">
        {reader === 'local' ? t('modal.readerLocal') : t('modal.readerVisionApi')}
      </span>
      {verified ? (
        <span
          className="ml-1.5 rounded-full bg-green/15 px-2 py-0.5 text-[9.5px] font-bold uppercase text-green"
          title={t('modal.weightsVerifiedTitle')}
        >
          {t('modal.weightsVerified')}
        </span>
      ) : null}
    </>
  );
}

/**
 * KAS nesutampa, o ne tik KAD kažkas nesutampa.
 *
 * `manual_review_needed` yra vienas bitas ir dengia keturias nesusijusias
 * priežastis; core/invoicing.py tai apgailestauja savo komentare — "nothing
 * downstream can say WHICH one fired". Šitos eilutės yra tas trūkstamas
 * atsakymas, ir dispečeriui jos yra visas skirtumas tarp "kažkas ne taip su
 * šiuo reisu" ir "užsakyta 10 t atsijų, atvežta 18 t smėlio".
 *
 * Radiniai gyvena 'ticket_ocr' įvykio detalėse (core/waybill_cross_check.py),
 * todėl senesnis nuskaitymas jų tiesiog neturi ir nieko nerodo — o ne rodo
 * "0 neatitikimų", ko niekas netikrino.
 */
function Findings({ findings }) {
  const t = useT();
  if (!Array.isArray(findings) || findings.length === 0) return null;
  return (
    <ul className="mt-1 w-full space-y-0.5 border-l-2 border-amber/40 pl-2 text-[11px] text-amber">
      {findings.map((finding, index) => (
        <li key={`${finding.kind}-${index}`}>
          {finding.kind === 'weight_mismatch'
            ? t('modal.findingWeight', {
              expected: finding.expected ?? '—',
              found: finding.found ?? '—',
              pct: finding.deviation_pct ?? '—',
            })
            : finding.kind === 'material_mismatch'
              ? t('modal.findingMaterial', {
                expected: finding.expected ?? '—',
                found: finding.found ?? '—',
              })
              : t('modal.findingDuplicate')}
        </li>
      ))}
    </ul>
  );
}

function OrderHistory({ orderId, lang }) {
  const t = useT();
  const { data: events = [], isLoading } = useOrderEvents(orderId);

  return (
    <section>
      <h3 className="panel-title">{t('modal.historyHeading')}</h3>
      {isLoading ? <SkeletonRows rows={3} /> : null}
      {!isLoading && events.length === 0 ? (
        <p className="text-[11.5px] text-dim">{t('modal.historyEmpty')}</p>
      ) : null}
      {events.length > 0 ? (
        <ul className="space-y-1 text-[11.5px]">
          {events.map((event) => (
            <li key={event.event_id} className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-semibold">
                {t(`orderEvent.${event.event_type}`)}
                <ReaderNote detail={event.detail} />
              </span>
              <span className="text-dim">
                {event.actor_name ? (
                  event.actor_name
                ) : event.actor_role === 'system' ? (
                  t('modal.historyBySystem')
                ) : (
                  <span className="italic" title={t('modal.historyUnnamedTitle')}>
                    {t('modal.historyUnnamed')}
                  </span>
                )}
                {' · '}
                {formatOrderDateTime(event.created_at, lang)}
              </span>
              <Findings findings={event.detail?.findings} />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * V-69. Telling the driver again, and telling him what changed.
 *
 * WHAT WAS MISSING. Two routes carry docstrings naming the exact screen
 * they belong to — "Admin action from the Užsakymai tab's order-detail
 * modal" — and nothing in frontend/src called either. One re-sends the
 * whole load (quarry, site, product, weight, nav link); the other sends the
 * order's note. The first names its own cases: a driver who missed the
 * dispatch, was reassigned after it went out, or just needs a reminder.
 *
 * So a dispatcher whose driver rang at 09:00 saying "I never got it" had no
 * button, and the only recourse was the telephone — the done-for-you cost
 * this audit exists to remove. And the note was worse than unreachable: it
 * is written on the create form, sent once automatically, and then shown on
 * NO screen and editable nowhere. A site that moves the unloading gate at
 * 09:05 could not reach the driver at all.
 *
 * A 201 IS NOT A DELIVERY, and this block must never read as one. The reply
 * is the telegram_messages row that was just written, and `status` is the
 * real answer:
 *
 *  - `sent` — it went.
 *  - `failed` — Telegram refused it, and error_detail says why.
 *  - `skipped_unlinked` — the driver has never linked his Telegram, so
 *    nothing was even attempted. That is the commonest cause of "I never
 *    got it", it is not a failure of this button, and it has a different
 *    fix (the driver's own link/QR on the Vairuotojai tab). Reporting it as
 *    "sent" would send a dispatcher back to the telephone convinced the
 *    message had gone.
 *
 * THE NOTE STOPS BEING EDITABLE WHEN THE LOAD IS DELIVERED, because
 * PUT /api/orders/<id> refuses a terminal order and is right to: a
 * delivered load's instructions are the historical record. The textarea is
 * simply not offered then, rather than offered and refused.
 */
function DriverMessage({ order, orderId }) {
  const t = useT();
  const send = useSendOrderTelegram(orderId);
  const update = useUpdateOrder();
  const [note, setNote] = useState(order?.note ?? '');
  const [savedNote, setSavedNote] = useState(order?.note ?? '');

  // No driver, nothing to message. A load waiting for assignment is a real
  // and common state, and buttons that cannot do anything are worse than
  // absent ones.
  if (!order?.driver_id) return null;

  const terminal = order.order_status === 'delivered' || order.order_status === 'cancelled';
  const dirty = note.trim() !== (savedNote ?? '').trim();

  const sendNote = () => {
    // Saved first, then sent: the route sends whatever is CURRENTLY stored,
    // so sending an unsaved edit would deliver the old text and leave the
    // dispatcher certain he had sent the new one.
    if (dirty) {
      update.mutate({ orderId, note: note.trim() || null }, {
        onSuccess: () => {
          setSavedNote(note.trim());
          send.mutate('note');
        },
      });
      return;
    }
    send.mutate('note');
  };

  return (
    <section>
      <h3 className="panel-title">{t('modal.driverMessageHeading')}</h3>

      {terminal ? (
        <p className="mb-1.5 text-[11px] leading-snug text-dim">{t('modal.noteLocked')}</p>
      ) : (
        <label className="mb-1.5 block">
          <span className="mb-1 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
            {t('modal.noteLabel')}
          </span>
          <textarea
            className="field min-h-[56px] w-full"
            value={note}
            placeholder={t('orders.notePh')}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn !px-2.5 !py-1 !text-[11px]"
          disabled={send.isPending || update.isPending}
          onClick={() => send.mutate('info')}
        >
          {t('modal.resendLoadBtn')}
        </button>
        {/* Offered only when there is a note to send. The route refuses an
            empty one with "order has no note to send", and a button that
            exists to be refused is a button that teaches people not to
            press buttons. */}
        {note.trim() && !terminal ? (
          <button
            type="button"
            className="btn !px-2.5 !py-1 !text-[11px]"
            disabled={send.isPending || update.isPending}
            onClick={sendNote}
          >
            {dirty ? t('modal.saveAndSendNoteBtn') : t('modal.sendNoteBtn')}
          </button>
        ) : null}
      </div>

      {/* The logged row's own verdict, never the 201. */}
      {send.data ? <SendVerdict row={send.data} t={t} /> : null}
      {send.isError ? <ErrorState error={send.error} /> : null}
      {update.isError ? <ErrorState error={update.error} /> : null}
    </section>
  );
}

function SendVerdict({ row, t }) {
  if (row.status === 'sent') {
    return (
      <p role="status" className="mt-1.5 text-[11px] leading-snug text-green">
        {t('modal.telegramSent')}
      </p>
    );
  }
  if (row.status === 'skipped_unlinked') {
    // Not a failure of this button, and a different fix. Saying "sent" here
    // would send a dispatcher back to the telephone certain the message had
    // gone out.
    return (
      <p role="alert" className="mt-1.5 text-[11px] leading-snug text-amber">
        {t('modal.telegramUnlinked')}
      </p>
    );
  }
  return (
    <p role="alert" className="mt-1.5 text-[11px] leading-snug text-red">
      {t('modal.telegramFailed')}
      {row.error_detail ? <span className="mt-0.5 block text-dim">{row.error_detail}</span> : null}
    </p>
  );
}

/**
 * V-66. What the quarry charged for the stone, against what the carrier's
 * own price list says it should have.
 *
 * WHAT WAS MISSING. GET /api/orders/<id>/price-estimate has been
 * admin-scoped and tested since the pricing work landed, and nothing in
 * frontend/src called it. The quarry's own unit price and total are read
 * off the weighbridge ticket by OCR and stored on the order — and were
 * rendered on no screen either. So the two halves of the purchase side both
 * existed and neither was ever shown, let alone compared.
 *
 * Measured on a live server: price list 9.80 EUR/t, the quarry's waybill
 * 10.60 EUR/t, 26.4 t weighed. 21.12 EUR on one load. Sixty loads a month
 * is 1 267.20 EUR the carrier pays without knowing he is paying it.
 *
 * A DIFFERENCE IS NOT AN ACCUSATION, and the wording here is careful about
 * it. The quarry may have raised its prices legitimately; the carrier's own
 * list may simply be out of date. What the product knows is that two
 * numbers about one load disagree, and by how much. It says that and names
 * both sides — deciding which is wrong is a telephone call, and this screen
 * is what prompts it.
 *
 * FOUR STATES, and each is a different sentence rather than a blank:
 *
 *  - The waybill carries no price at all. OCR often does not read the money
 *    columns, and a hand-entered waybill has none. There is nothing to
 *    compare, and showing the price-list figure by itself would look like a
 *    check that had passed.
 *  - No price on file for this material at this quarry (`matched: false`).
 *    That is a gap in the price list, and it is also why every margin figure
 *    for this load is missing — one fix, said once.
 *  - The comparison rests on the DISPATCHER'S ESTIMATED weight rather than a
 *    weighbridge figure. Then the euro difference is an estimate too, and
 *    it says so instead of presenting a guess as an audit.
 *  - They agree. Worth saying out loud: a carrier who only ever sees this
 *    block when something is wrong learns nothing from its absence.
 */
function PurchasePriceCheck({ order, orderId }) {
  const t = useT();
  const { data: estimate, isLoading } = useOrderPriceEstimate(orderId);

  // Transporto kainodaros modeliai: this whole comparison is "what the
  // quarry charged per tonne" against "what our own price list says per
  // tonne" — a per_ton-specific question. A per_km/flat/hourly/ton_km order
  // has no per-tonne price list to check the waybill against at all, so
  // there is nothing here for those four methods to render; showing
  // estimate.price_per_ton as "—/t" beside a real per_km amount would
  // answer a question about tonnage that this load was never priced on.
  if (order.pricing_method && order.pricing_method !== "per_ton") return null;

  // The quarry's own figures, off its ticket. total_sum is the net — the
  // schema keeps total_with_vat separately — so it is compared against
  // price_be_pvm and never against the VAT-inclusive figure beside it.
  const charged = order?.total_sum ?? null;
  if (isLoading || !estimate) return null;
  if (charged === null && !estimate.matched) return null;

  const expected = estimate.matched ? estimate.price_be_pvm : null;
  const difference =
    charged !== null && expected !== null ? Math.round((charged - expected) * 100) / 100 : null;

  return (
    <section>
      <h3 className="panel-title">{t('modal.purchaseHeading')}</h3>
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11.5px]">
        <ReadRow
          label={t('modal.purchaseCharged')}
          value={charged === null ? null : `${eurExact(charged)}${
            order.unit_price ? ` · ${eurExact(order.unit_price)}/t` : ''}`}
        />
        <ReadRow
          label={t('modal.purchaseExpected')}
          value={expected === null ? null : `${eurExact(expected)} · ${
            eurExact(estimate.price_per_ton)}/t`}
        />
      </div>

      {/* One sentence, chosen by which of the four states this load is in.
          Ordered by what a reader can act on: no price list first, because
          it is the one that also breaks every margin figure for this load. */}
      {!estimate.matched ? (
        <p className="mt-1.5 text-[11px] leading-snug text-amber">
          {t('modal.purchaseNoPriceOnFile', { material: order.cargo_type ?? '' })}
        </p>
      ) : charged === null ? (
        <p className="mt-1.5 text-[11px] leading-snug text-dim">
          {t('modal.purchaseNothingToCompare')}
        </p>
      ) : difference === 0 ? (
        <p className="mt-1.5 text-[11px] leading-snug text-green">
          {t('modal.purchaseAgrees')}
        </p>
      ) : (
        <p className={`mt-1.5 text-[11px] leading-snug ${difference > 0 ? 'text-red' : 'text-accent'}`}>
          {t(difference > 0 ? 'modal.purchaseOver' : 'modal.purchaseUnder', {
            eur: eurExact(Math.abs(difference)),
          })}
          {/* The weight the comparison rests on. An estimate makes the euro
              figure an estimate, and a screen that hid that would turn a
              dispatcher's guess into a claim against a supplier. */}
          {estimate.weight_source === 'estimated' ? (
            <span className="mt-0.5 block text-dim">{t('modal.purchaseOnEstimate')}</span>
          ) : null}
        </p>
      )}
    </section>
  );
}

/**
 * V-63. The stops this load actually made, and the one field on them a
 * dispatcher can still change.
 *
 * WHAT WAS MISSING. GET /api/orders/<id>/dwell-records and
 * PATCH /api/dwell-records/<id>/reason have been admin-scoped and tested
 * since the dwell engine shipped, and nothing in the console called either.
 * So a load's own waiting could only be read as a fleet-wide monthly
 * aggregate, and the reason a truck waited — which the Telegram picker
 * interrupts a driver at the wheel to ask for — could never be corrected
 * once the stop had ended.
 *
 * V-62 put those reasons on the Analytics tab, which is what makes this the
 * next thing needed rather than a nicety: a carrier can now read
 * "Nepaaiškinta · 1 sustojimas · 1,00 val. · €35" and still have nowhere to
 * explain it, and a driver's mis-tap ("Gedimas" for what was a queue at the
 * client's gate) is permanent.
 *
 * A WRONG REASON DOES NOT MOVE THE MONEY, and this section must not imply
 * that it does. core/dwell_engine splits billable from fleet loss on
 * location_type, never on reason. What it moves is the DECISION: whether to
 * put this stop on the client's invoice at all — a breakdown at their gate
 * is the carrier's own fault and billing it invites a dispute he loses —
 * and which supplier the month's waiting is worth a telephone call about.
 *
 * THE FIGURES ARE READ-ONLY AND THAT IS DELIBERATE. Arrival, departure, the
 * free allowance and the excess are measurements, and the allowance is
 * frozen onto the row when the stop opens precisely so a later settings
 * change cannot reprice an act already sent to a client. Only the reason is
 * a human's account of events, so only the reason is editable.
 */
function OrderStops({ orderId, lang }) {
  const t = useT();
  const { data: stops = [], isLoading } = useOrderDwellRecords(orderId);
  const setReason = useSetDwellReason(orderId);
  const waive = useWaiveDwellRecord(orderId);

  if (!isLoading && stops.length === 0) return null;

  return (
    <section>
      <h3 className="panel-title">{t('modal.stopsHeading')}</h3>
      {isLoading ? <SkeletonRows rows={2} /> : null}
      {stops.length > 0 ? (
        <ul className="space-y-2 text-[11.5px]">
          {stops.map((stop) => (
            <li key={stop.id} className="rounded-[11px] border border-line bg-white/2 p-2.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-semibold">
                  {t(`dwell.location.${stop.location_type}`)}
                </span>
                <span className="text-dim">
                  {formatOrderDateTime(stop.arrived_at, lang)}
                  {stop.departed_at ? ` – ${formatOrderDateTime(stop.departed_at, lang)}`
                    : ` · ${t('modal.stopStillOpen')}`}
                </span>
              </div>
              <p className="mt-0.5 text-dim">
                {/* The allowance, then what ran past it, then what that is
                    worth — in that order, because it is the order the
                    sentence has to be read in to mean anything. A bare
                    "45 min" says nothing about whether anybody owes for it.

                    excess of 0 prints as 0 and not as an em-dash: this stop
                    was measured and fitted inside the free time, which is a
                    fact, not a gap. The COST is the em-dash case — null
                    there means no rate was set when the stop opened, and
                    €0,00 would say the waiting was free. */}
                {t('modal.stopAllowance', { n: stop.free_allowance_minutes ?? 0 })}
                {' · '}
                {t('modal.stopExcess', { n: stop.excess_dwell_minutes ?? 0 })}
                {' · '}
                {stop.total_cost_eur === null || stop.total_cost_eur === undefined
                  ? t('analytics.dwellNotPriced')
                  : eurExact(stop.total_cost_eur)}
                {stop.waived ? ` · ${t('modal.stopWaived')}` : null}
              </p>
              <label className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.03em] text-dim">
                  {t('analytics.colReason')}
                </span>
                <select
                  className="field !w-auto !py-1 !text-[11px]"
                  value={stop.reason ?? ''}
                  disabled={setReason.isPending}
                  onChange={(e) => setReason.mutate({
                    dwellRecordId: stop.id, reason: e.target.value,
                  })}
                >
                  {/* The empty option is what the row currently IS when
                      nobody answered, and it is not selectable back: the
                      route requires a reason, and "un-explain this stop"
                      is not an act anybody needs. Shown so the picker can
                      display the true current state rather than silently
                      claiming the first reason in the list. */}
                  <option value="" disabled>{t('dwell.reason.unexplained')}</option>
                  {DWELL_REASONS.map((reason) => (
                    <option key={reason} value={reason}>{t(`dwell.reason.${reason}`)}</option>
                  ))}
                </select>
              </label>
              {/* Transporto kainodaros modeliai: only a CLOSED stop with a
                  real, still-unwaived cost has anything to waive — an open
                  stop has no total_cost_eur yet, and the server itself
                  refuses a waive on one (see blueprints/orders.py). */}
              {stop.departed_at && stop.total_cost_eur > 0 && !stop.waived ? (
                <button
                  type="button"
                  className="btn btn-sm mt-1.5"
                  disabled={waive.isPending}
                  onClick={() => {
                    const reason = window.prompt(t('modal.waiveDwellPrompt')) ?? undefined;
                    if (reason === undefined) return; // cancelled
                    waive.mutate({ dwellRecordId: stop.id, reason: reason || null });
                  }}
                >
                  {t('modal.waiveDwellBtn')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {setReason.isError ? <ErrorState error={setReason.error} /> : null}
      {waive.isError ? <ErrorState error={waive.error} /> : null}
    </section>
  );
}

function ReadRow({ label, value }) {
  return (
    <>
      <span className="text-dim">{label}</span>
      <span className="text-right">{value || '—'}</span>
    </>
  );
}
